import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import monitor, {
  AlertData,
  DeviceOrientation,
  EnabledVitalSigns,
  FaceSessionOptions,
  HealthMonitorCodes,
  HealthMonitorSession,
  ImageValidity,
  OfflineMeasurements,
  SessionState,
  VitalSigns,
  VitalSignsResults,
} from '@binah/web-sdk';
import { InfoType, InfoData } from '../types';
import toast from 'react-hot-toast';
import useFaceScanStore from 'store/face-scan';
import useAuthStore from 'store/auth';
import { getSubjectDemographic } from 'lib/utils';
import { patchSessionForVideoRecording } from 'lib/rgb-video/video-recording-patch';
import { patchSessionForRgbCollection } from 'lib/rgb-video/rgb-collection-patch';

const useMonitor = (
  video: MutableRefObject<HTMLVideoElement>,
  cameraId: string,
  processingTime: number,
  licenseKey: string,
  productId: string,
  startMeasuring: boolean,
  onComplete: (results: VitalSignsResults) => void,
  handleValidityJSON: (validity: string) => void
) => {
  const userAttributes = useAuthStore((store) => store.userAttributes);
  const includeDemographic = useFaceScanStore(
    (store) => store.includeDemographic
  );

  const [session, setSession] = useState<HealthMonitorSession>();
  const [sessionState, setSessionState] = useState<SessionState>();
  const [isMonitorReady, setIsMonitorReady] = useState<boolean>();
  const [enabledVitalSigns, setEnabledVitalSigns] =
    useState<EnabledVitalSigns>();
  const [offlineMeasurements, setOfflineMeasurements] =
    useState<OfflineMeasurements>();
  const [vitalSigns, setVitalSigns] = useState<VitalSigns | null>();

  const [error, setError] = useState<AlertData>({ code: -1 });
  const [warning, setWarning] = useState<AlertData>({ code: -1 });
  const [info, setInfo] = useState<InfoData>({ type: InfoType.NONE });
  const isDismissing = useRef<boolean>(false);

  const subjectDemographic = getSubjectDemographic(userAttributes);

  const setInfoWithDismiss = useCallback(
    (info: InfoData, seconds?: number) => {
      if (!isDismissing.current) {
        setInfo(info);
        if (seconds) {
          isDismissing.current = true;
          setTimeout(() => {
            setInfo({ type: InfoType.NONE });
            isDismissing.current = false;
          }, seconds * 1000);
        }
      }
    },
    [InfoType, setInfo, info, isDismissing, isDismissing.current]
  );

  const updateVitalSigns = useCallback((vitalSigns: VitalSigns) => {
    setVitalSigns((prev) => ({
      ...prev,
      ...vitalSigns,
    }));
  }, []);

  const onVitalSign = useCallback((vitalSign: VitalSigns) => {
    updateVitalSigns(vitalSign);
  }, []);

  const onFinalResults = useCallback((vitalSignsResults: VitalSignsResults) => {
    onComplete(vitalSignsResults);
    updateVitalSigns(vitalSignsResults.results);
  }, []);

  const onError = (errorData: AlertData) => {
    setError(errorData);
  };

  const onWarning = (warningData: AlertData) => {
    if (
      warningData.code ===
      HealthMonitorCodes.MEASUREMENT_CODE_MISDETECTION_DURATION_EXCEEDS_LIMIT_WARNING
    ) {
      setVitalSigns(null);
    }
    setWarning(warningData);
  };

  const onStateChange = useCallback((state: SessionState) => {
    setSessionState(state);
    if (state === SessionState.MEASURING) {
      setVitalSigns(null);
    }
  }, []);

  const onEnabledVitalSigns = useCallback((vitalSigns: EnabledVitalSigns) => {
    setEnabledVitalSigns(vitalSigns);
  }, []);

  const onOfflineMeasurement = useCallback(
    (offlineMeasurements: OfflineMeasurements) => {
      setOfflineMeasurements(offlineMeasurements);
    },
    []
  );

  const onActivation = useCallback(() => {
    console.info('The device has been activated');
  }, []);

  const onImageData = useCallback((imageValidity: ImageValidity) => {
    let message: string;
    let instruction: string;
    if (imageValidity != ImageValidity.VALID) {
      switch (imageValidity) {
        case ImageValidity.INVALID_DEVICE_ORIENTATION:
          message = 'Unsupported Orientation';
          instruction = '';
          break;
        case ImageValidity.TILTED_HEAD:
          message = 'Head Tilted';
          instruction = 'Please make sure your head is straight to camera';
          break;
        case ImageValidity.FACE_TOO_FAR: // Placeholder, currently not supported
          message = 'You Are Too Far';
          instruction = 'Please make sure you are not too far from camera';
          break;
        case ImageValidity.UNEVEN_LIGHT:
          message = 'Uneven Lighting';
          instruction = 'Please make sure your lighting is proper';
          break;
        case ImageValidity.INVALID_ROI:
        default:
          message = 'Face Not Detected';
          instruction = 'Please make sure your face is visible';
      }
      setInfo({
        type: InfoType.INSTRUCTION,
        message: message,
        instruction: instruction,
      });
      handleValidityJSON(ImageValidity[imageValidity]);
    } else {
      setInfoWithDismiss({ type: InfoType.NONE });
    }
  }, []);

  useEffect(() => {
    (async () => {
      if (!licenseKey) return;

      try {
        await monitor.initialize({
          licenseKey,
          licenseInfo: {
            onEnabledVitalSigns,
            onOfflineMeasurement,
            onActivation,
          },
        });
        console.info(`Initialized monitor`);
        setIsMonitorReady(true);
        setError({ code: -1 });
      } catch (e) {
        const error = e as unknown as { errorCode: number; message: string };
        setIsMonitorReady(false);
        setError({ code: error?.errorCode });
        toast.error(error.message);
      }
    })();
  }, [licenseKey, productId]);

  useEffect(() => {
    (async () => {
      try {
        if (!isMonitorReady || !processingTime || !video.current) {
          return;
        }

        sessionState === SessionState.ACTIVE && session?.terminate();

        const options: FaceSessionOptions = {
          input: video.current,
          cameraDeviceId: cameraId,
          processingTime,
          onVitalSign,
          onFinalResults,
          onError,
          onWarning,
          onStateChange,
          orientation: DeviceOrientation.PORTRAIT,
          onImageData,
          isAnalyticsEnabled: Boolean(includeDemographic && subjectDemographic),
          ...(includeDemographic && { subjectDemographic }),
          /*******************************************************************************
           * For accurate vital signs calculation the user's parameters should be provided.
           * The following is an example of how the parameters are passed to the SDK:
           *
           * subjectDemographic: {sex: Sex.MALE, age: 35, weight: 75, height: 165}
           *
           * When measuring a new user then a new session must be created using the
           * new user's parameters
           * The parameters are used locally on the device only for the vital sign
           * calculation, and deleted when the session is terminated.
           *******************************************************************************/
        };

        const faceSession = await monitor.createFaceSession(options);
        const patchedSession = patchSessionForVideoRecording(
          faceSession,
          video.current
        );
        // Patch session for RGB signal collection (same format as Android)
        patchSessionForRgbCollection(patchedSession, video.current);
        console.info(`Session patched for video recording and RGB collection`);

        setSession(patchedSession);
        setError({ code: -1 });
      } catch (error) {
        const e = error as unknown as { errorCode: number; message: string };
        setError({ code: e?.errorCode });
        toast.error(e.message);
      }
    })();
  }, [processingTime, isMonitorReady]);

  useEffect(() => {
    if (startMeasuring) {
      if (sessionState === SessionState.ACTIVE) {
        session?.start();
        setError({ code: -1 });
      }
    } else {
      sessionState === SessionState.MEASURING && session?.stop();
    }
  }, [startMeasuring]);

  return {
    sessionState,
    // vitalSigns: {
    //   heartRate: {
    //     value: vitalSigns?.heartRate?.value,
    //     isEnabled: enabledVitalSigns?.isEnabledHeartRate,
    //   },
    //   breathingRate: {
    //     value: vitalSigns?.breathingRate?.value,
    //     isEnabled: enabledVitalSigns?.isEnabledBreathingRate,
    //   },
    //   stress: {
    //     value: vitalSigns?.stressLevel?.value,
    //     isEnabled: enabledVitalSigns?.isEnabledStressLevel,
    //   },
    //   hrvSdnn: {
    //     value: vitalSigns?.sdnn?.value,
    //     isEnabled: enabledVitalSigns?.isEnabledSdnn,
    //   },
    //   spo2: {
    //     value: null, //TODO Spo2 is currently disabled by algo
    //     isEnabled: false, //enabledVitalSigns?.isEnabledSpo2,
    //   },
    //   bloodPressure: {
    //     value: vitalSigns?.bloodPressure?.value,
    //     isEnabled: enabledVitalSigns?.isEnabledBloodPressure,
    //   },
    // },
    vitalSigns: {
      pulseRate: {
        value: vitalSigns?.pulseRate?.value,
        isEnabled: enabledVitalSigns?.isEnabledPulseRate,
      },
      respirationRate: {
        value: vitalSigns?.respirationRate?.value,
        isEnabled: enabledVitalSigns?.isEnabledRespirationRate,
      },
      stress: {
        value: vitalSigns?.stressLevel?.value,
        isEnabled: enabledVitalSigns?.isEnabledStressLevel,
      },
      hrvSdnn: {
        value: vitalSigns?.sdnn?.value,
        isEnabled: enabledVitalSigns?.isEnabledSdnn,
      },
      spo2: {
        value: null, //TODO Spo2 is currently disabled by algo
        isEnabled: false, //enabledVitalSigns?.isEnabledSpo2,
      },
      bloodPressure: {
        value: vitalSigns?.bloodPressure?.value,
        isEnabled: enabledVitalSigns?.isEnabledBloodPressure,
      },
    },
    offlineMeasurements,
    error,
    warning,
    info,
  };
};

export default useMonitor;
